/**
 * Autopilot UI Component
 * Shows autopilot status and toggle button
 */

export class AutopilotUI {
    constructor(game) {
        this.game = game;
        this.container = null;
        this.statusContainer = null;
        this.toggleButton = null;
        this.platformButton = null;
        this.statusText = null;
        this.isVisible = false;
        this.autopilotOn = false;
        this.hasOre = false;
        this.createUI();
    }
    
    createUI() {
        // Create status container (positioned to the left of ore count - desktop only)
        this.statusContainer = document.createElement('div');
        this.statusContainer.id = 'autopilot-status';
        this.statusContainer.style.cssText = `
            position: fixed;
            top: 38px;
            right: 185px;
            background: rgba(0, 0, 0, 0.4);
            border: 1px solid var(--neon-blue, #01bdff);
            border-radius: 20px;
            padding: 6px 15px;
            color: var(--neon-blue, #01bdff);
            font-family: 'Poppins', sans-serif;
            font-size: 10px;
            z-index: 1000;
            display: none;
            box-shadow: 0 0 10px rgba(1, 189, 255, 0.3);
            backdrop-filter: blur(5px);
            pointer-events: none;
            white-space: nowrap;
        `;
        
        // Status text
        this.statusText = document.createElement('div');
        this.statusText.style.cssText = `
            font-weight: 600;
            font-size: 10px;
            text-align: center;
            letter-spacing: 0.5px;
        `;
        
        this.statusContainer.appendChild(this.statusText);
        document.body.appendChild(this.statusContainer);
        
        // Create toggle button (bottom right - always visible when game is active)
        this.toggleButton = document.createElement('button');
        this.toggleButton.id = 'autopilot-toggle';
        this.toggleButton.setAttribute('aria-label', 'Toggle Autopilot');
        this.toggleButton.style.cssText = `
            position: fixed;
            bottom: 20px;
            right: 20px;
            width: 48px;
            height: 48px;
            border-radius: 50%;
            border: 3px solid var(--neon-blue, #01bdff);
            background: rgba(0, 0, 0, 0.7);
            color: var(--neon-blue, #01bdff);
            font-size: 20px;
            cursor: pointer;
            z-index: 1001;
            display: none;
            box-shadow: 0 0 20px rgba(1, 189, 255, 0.4);
            backdrop-filter: blur(5px);
            transition: all 0.3s ease;
            outline: none;
            user-select: none;
            -webkit-tap-highlight-color: transparent;
        `;
        
        // Create active indicator (green circle)
        this.activeIndicator = document.createElement('div');
        this.activeIndicator.id = 'autopilot-indicator';
        this.activeIndicator.style.cssText = `
            position: absolute;
            top: -4px;
            right: -4px;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: var(--green, #00ff22);
            border: 2px solid rgba(0, 0, 0, 0.8);
            box-shadow: 0 0 10px rgba(0, 255, 34, 0.8);
            display: none;
            z-index: 1002;
        `;
        
        // Mobile adjustments
        const mobileStyles = `
            @media (max-width: 768px) {
                #autopilot-toggle {
                    width: 40px;
                    height: 40px;
                    font-size: 16px;
                    bottom: 15px;
                    right: 15px;
                    border-width: 2px;
                }
                #autopilot-indicator {
                    width: 12px;
                    height: 12px;
                    top: -3px;
                    right: -3px;
                    border-width: 1.5px;
                }
            }
            @media (max-width: 896px) and (orientation: landscape) {
                #autopilot-toggle {
                    width: 36px;
                    height: 36px;
                    font-size: 14px;
                    bottom: 10px;
                    right: 10px;
                    border-width: 2px;
                }
                #autopilot-indicator {
                    width: 10px;
                    height: 10px;
                    top: -2px;
                    right: -2px;
                    border-width: 1px;
                }
            }
        `;
        
        // Add mobile styles
        const style = document.createElement('style');
        style.textContent = `
            #autopilot-toggle {
                position: relative;
            }
            #autopilot-toggle:hover {
                background: rgba(1, 189, 255, 0.2);
                box-shadow: 0 0 30px rgba(1, 189, 255, 0.6);
                transform: scale(1.1);
            }
            #autopilot-toggle:active {
                transform: scale(0.95);
            }
            #autopilot-toggle.active {
                border-color: var(--green, #00ff22);
                color: var(--green, #00ff22);
                background: rgba(0, 255, 34, 0.2);
                box-shadow: 0 0 30px rgba(0, 255, 34, 0.8);
            }
            #autopilot-indicator.active {
                display: block;
                animation: pulseGreen 2s ease-in-out infinite;
            }
            @keyframes pulseGreen {
                0%, 100% {
                    opacity: 1;
                    transform: scale(1);
                }
                50% {
                    opacity: 0.7;
                    transform: scale(1.1);
                }
            }
            ${mobileStyles}
        `;
        document.head.appendChild(style);
        
        // Set initial icon
        this.toggleButton.textContent = '🤖';
        
        // Append indicator to button
        this.toggleButton.appendChild(this.activeIndicator);
        
        // Add click/touch handler
        this.toggleButton.addEventListener('click', () => {
            if (this.game && typeof this.game.toggleAutopilot === 'function') {
                this.game.toggleAutopilot();
            }
        });
        
        // Prevent double-tap zoom on mobile
        this.toggleButton.addEventListener('touchend', (e) => {
            e.preventDefault();
            this.toggleButton.click();
        });
        
        document.body.appendChild(this.toggleButton);
        
        // Create platform call button (to the left of autopilot button)
        this.platformButton = document.createElement('button');
        this.platformButton.id = 'platform-call-toggle';
        this.platformButton.setAttribute('aria-label', 'Call Platform');
        this.platformButton.style.cssText = `
            position: fixed;
            bottom: 20px;
            right: 78px;
            width: 48px;
            height: 48px;
            border-radius: 50%;
            border: 3px solid var(--yellow, #ffbe00);
            background: rgba(0, 0, 0, 0.7);
            color: var(--yellow, #ffbe00);
            font-size: 24px;
            cursor: pointer;
            z-index: 1001;
            display: none;
            box-shadow: 0 0 20px rgba(255, 190, 0, 0.4);
            backdrop-filter: blur(5px);
            transition: all 0.3s ease;
            outline: none;
            user-select: none;
            -webkit-tap-highlight-color: transparent;
        `;
        
        // Add platform button styles
        const platformStyle = document.createElement('style');
        platformStyle.textContent = `
            #platform-call-toggle {
                position: relative;
            }
            #platform-call-toggle:hover {
                background: rgba(255, 190, 0, 0.2);
                box-shadow: 0 0 30px rgba(255, 190, 0, 0.6);
                transform: scale(1.1);
            }
            #platform-call-toggle:active {
                transform: scale(0.95);
            }
            @media (max-width: 768px) {
                #platform-call-toggle {
                    width: 40px;
                    height: 40px;
                    font-size: 20px;
                    bottom: 15px;
                    right: 63px;
                    border-width: 2px;
                }
            }
            @media (max-width: 896px) and (orientation: landscape) {
                #platform-call-toggle {
                    width: 36px;
                    height: 36px;
                    font-size: 18px;
                    bottom: 10px;
                    right: 54px;
                    border-width: 2px;
                }
            }
        `;
        document.head.appendChild(platformStyle);
        
        // Set platform icon (using a platform emoji or symbol)
        this.platformButton.innerHTML = '🛬'; // Landing/platform icon
        
        // Add click/touch handler for platform button
        this.platformButton.addEventListener('click', () => {
            if (this.game && typeof this.game.togglePlatform === 'function') {
                this.game.togglePlatform();
            }
        });
        
        // Prevent double-tap zoom on mobile
        this.platformButton.addEventListener('touchend', (e) => {
            e.preventDefault();
            this.platformButton.click();
        });
        
        document.body.appendChild(this.platformButton);
    }
    
    show(autopilotOn) {
        this.isVisible = true;
        this.autopilotOn = autopilotOn;
        
        // Show status container on desktop only
        if (window.innerWidth > 768) {
            this.statusContainer.style.display = 'block';
        } else {
            this.statusContainer.style.display = 'none';
        }
        
        // Show toggle button on mobile only
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                         window.innerWidth < 768;
        if (isMobile) {
            this.toggleButton.style.display = 'block';
        } else {
            this.toggleButton.style.display = 'none';
        }
        
        // Update platform button visibility based on ore (mobile only)
        this.updatePlatformButtonVisibility();
        
        this.updateStatus(autopilotOn);
    }
    
    hide() {
        this.isVisible = false;
        this.statusContainer.style.display = 'none';
        this.toggleButton.style.display = 'none';
        this.platformButton.style.display = 'none';
    }
    
    // Update platform button visibility based on ore amount
    updateOreStatus(hasOre) {
        this.hasOre = hasOre;
        if (this.isVisible) {
            this.updatePlatformButtonVisibility();
        }
    }
    
    updatePlatformButtonVisibility() {
        // Only show platform button on mobile
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                         window.innerWidth < 768;
        if (this.hasOre && isMobile) {
            this.platformButton.style.display = 'block';
        } else {
            this.platformButton.style.display = 'none';
        }
    }
    
    updateStatus(autopilotOn) {
        this.autopilotOn = autopilotOn;
        
        if (autopilotOn) {
            // Autopilot ON
            this.statusText.textContent = '🤖 AUTOPILOT ACTIVE';
            this.statusText.style.color = 'var(--green, #00ff22)';
            this.statusContainer.style.borderColor = 'var(--green, #00ff22)';
            this.statusContainer.style.boxShadow = '0 0 10px rgba(0, 255, 34, 0.5)';
            
            // Update button - show active state (green) and green indicator
            this.toggleButton.classList.add('active');
            this.toggleButton.style.borderColor = 'var(--green, #00ff22)';
            this.toggleButton.style.color = 'var(--green, #00ff22)';
            this.toggleButton.style.background = 'rgba(0, 255, 34, 0.2)';
            this.toggleButton.style.boxShadow = '0 0 30px rgba(0, 255, 34, 0.8)';
            this.toggleButton.textContent = '🤖';
            this.activeIndicator.classList.add('active');
        } else {
            // Manual control
            this.statusText.textContent = '👤 PRESS P FOR AUTOPILOT';
            this.statusText.style.color = 'var(--neon-blue, #01bdff)';
            this.statusContainer.style.borderColor = 'var(--neon-blue, #01bdff)';
            this.statusContainer.style.boxShadow = '0 0 10px rgba(1, 189, 255, 0.3)';
            
            // Update button - remove active state (back to blue)
            this.toggleButton.classList.remove('active');
            this.toggleButton.style.borderColor = 'var(--neon-blue, #01bdff)';
            this.toggleButton.style.color = 'var(--neon-blue, #01bdff)';
            this.toggleButton.style.background = 'rgba(0, 0, 0, 0.7)';
            this.toggleButton.style.boxShadow = '0 0 20px rgba(1, 189, 255, 0.4)';
            this.toggleButton.textContent = '🤖';
            this.activeIndicator.classList.remove('active');
        }
    }
    
    destroy() {
        if (this.statusContainer && this.statusContainer.parentNode) {
            this.statusContainer.parentNode.removeChild(this.statusContainer);
        }
        if (this.toggleButton && this.toggleButton.parentNode) {
            this.toggleButton.parentNode.removeChild(this.toggleButton);
        }
        if (this.platformButton && this.platformButton.parentNode) {
            this.platformButton.parentNode.removeChild(this.platformButton);
        }
    }
}


